import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Card, CardContent, CardDescription, CardHeader, CardTitle, Input, Badge } from '../components/ui';
import { api } from '../lib/api';
import { toast } from '../stores/toastStore';

interface AdminTenantItem {
  id: string;
  name: string;
  email: string;
  status: 'active' | 'trial' | 'expired' | 'suspended' | 'cancelled';
  subscriptionPlan?: string;
  trialEndsAt?: string | null;
  messageLimit?: number;
  isPartner: boolean;
  partnerTenantId?: string | null;
  portalDomains: string[];
  partnerKey?: string | null;
  createdAt: string;
}

const ADMIN_TOKEN_KEY = 'admin_token';
const ADMIN_EXPIRES_KEY = 'admin_token_expires_at';

export function Admin() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [adminToken, setAdminToken] = useState<string | null>(null);
  const [tenants, setTenants] = useState<AdminTenantItem[]>([]);
  const [isLoadingTenants, setIsLoadingTenants] = useState(false);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [generatingKeyTenantId, setGeneratingKeyTenantId] = useState<string | null>(null);
  const [notifyingTenantId, setNotifyingTenantId] = useState<string | null>(null);
  const [unlinkingPartnerTenantId, setUnlinkingPartnerTenantId] = useState<string | null>(null);
  const [addingFreeDaysTenantId, setAddingFreeDaysTenantId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const token = localStorage.getItem(ADMIN_TOKEN_KEY);
    const expiresAt = localStorage.getItem(ADMIN_EXPIRES_KEY);

    if (!token || !expiresAt) return;
    if (Date.now() > new Date(expiresAt).getTime()) {
      localStorage.removeItem(ADMIN_TOKEN_KEY);
      localStorage.removeItem(ADMIN_EXPIRES_KEY);
      return;
    }

    setAdminToken(token);
  }, []);

  const authHeader = useMemo(() => {
    if (!adminToken) return undefined;
    return `Bearer ${adminToken}`;
  }, [adminToken]);

  const loadTenants = useCallback(async () => {
    if (!authHeader) return;

    setIsLoadingTenants(true);
    try {
      const result = await api.getAdminTenants(authHeader);
      setTenants(result);
    } catch (error: any) {
      toast.error(error?.message || 'Falha ao carregar tenants.', 'Admin');
      if ((error?.message || '').toLowerCase().includes('não autorizado') || (error?.message || '').toLowerCase().includes('unauthorized')) {
        localStorage.removeItem(ADMIN_TOKEN_KEY);
        localStorage.removeItem(ADMIN_EXPIRES_KEY);
        setAdminToken(null);
      }
    } finally {
      setIsLoadingTenants(false);
    }
  }, [authHeader]);

  useEffect(() => {
    void loadTenants();
  }, [loadTenants]);

  const handleLogin = async () => {
    if (!username.trim() || !password) return;

    setIsLoggingIn(true);
    try {
      const session = await api.adminLogin(username.trim(), password);
      localStorage.setItem(ADMIN_TOKEN_KEY, session.token);
      localStorage.setItem(ADMIN_EXPIRES_KEY, session.expiresAt);
      setAdminToken(session.token);
      setPassword('');
      toast.success('Login admin realizado com sucesso.', 'Admin');
    } catch (error: any) {
      toast.error(error?.message || 'Falha no login admin.', 'Admin');
    } finally {
      setIsLoggingIn(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem(ADMIN_TOKEN_KEY);
    localStorage.removeItem(ADMIN_EXPIRES_KEY);
    setAdminToken(null);
    setTenants([]);
  };

  const handleGeneratePartnerKey = async (tenantId: string) => {
    if (!authHeader) return;

    setGeneratingKeyTenantId(tenantId);
    try {
      const result = await api.generatePartnerKeyForTenant(authHeader, tenantId);
      toast.success(`Key gerada: ${result.partnerKey}`, 'Parceiro');
      await loadTenants();
    } catch (error: any) {
      toast.error(error?.message || 'Falha ao gerar key de parceiro.', 'Parceiro');
    } finally {
      setGeneratingKeyTenantId(null);
    }
  };

  const handleNotifyTenant = async (tenantId: string) => {
    if (!authHeader) return;

    const title = window.prompt('Título da notificação para este tenant:', 'Mensagem do administrador');
    if (title === null) return;

    const message = window.prompt('Mensagem que será enviada no sino do tenant:');
    if (message === null || !message.trim()) return;

    setNotifyingTenantId(tenantId);
    try {
      await api.adminNotifyTenant(authHeader, tenantId, title.trim(), message.trim());
      toast.success('Notificação enviada para o tenant.', 'Admin');
    } catch (error: any) {
      toast.error(error?.message || 'Falha ao enviar notificação.', 'Admin');
    } finally {
      setNotifyingTenantId(null);
    }
  };

  const handleUnlinkPartner = async (tenant: AdminTenantItem) => {
    if (!authHeader) return;

    const isPartnerWithClients = tenant.isPartner;
    const isClientLinkedToPartner = !!tenant.partnerTenantId;

    if (!isPartnerWithClients && !isClientLinkedToPartner) {
      toast.error('Este tenant não possui vínculo de parceiro ativo.', 'Parceiro');
      return;
    }

    const confirmation = window.confirm(
      isPartnerWithClients
        ? 'Deseja desvincular este parceiro de todos os clientes vinculados?'
        : 'Deseja remover o vínculo deste cliente com o parceiro?',
    );

    if (!confirmation) {
      return;
    }

    setUnlinkingPartnerTenantId(tenant.id);
    try {
      const result = await api.adminUnlinkPartner(authHeader, tenant.id);
      if (result.detachedClientsCount > 0) {
        toast.success(`Parceiro desvinculado de ${result.detachedClientsCount} cliente(s).`, 'Parceiro');
      } else if (result.unlinkedFromPartner) {
        toast.success('Cliente desvinculado do parceiro com sucesso.', 'Parceiro');
      } else {
        toast.success('Nenhum vínculo ativo para remover neste tenant.', 'Parceiro');
      }
      await loadTenants();
    } catch (error: any) {
      toast.error(error?.message || 'Falha ao desvincular parceiro.', 'Parceiro');
    } finally {
      setUnlinkingPartnerTenantId(null);
    }
  };

  const handleAddFreeDays = async (tenant: AdminTenantItem) => {
    if (!authHeader) return;

    const rawDays = window.prompt('Quantos dias de free deseja adicionar?', '3');
    if (rawDays === null) return;

    const days = Number(rawDays);
    if (!Number.isInteger(days) || days <= 0) {
      toast.error('Informe um número inteiro maior que zero.', 'Free Days');
      return;
    }

    setAddingFreeDaysTenantId(tenant.id);
    try {
      const result = await api.adminAddFreeDays(authHeader, tenant.id, days);
      const formattedTrialEnd = result.trialEndsAt
        ? new Date(result.trialEndsAt).toLocaleString('pt-BR')
        : 'n/a';
      toast.success(
        `${result.daysAdded} dia(s) adicionados. Trial até: ${formattedTrialEnd}`,
        'Free Days',
      );
      await loadTenants();
    } catch (error: any) {
      toast.error(error?.message || 'Falha ao adicionar dias free.', 'Free Days');
    } finally {
      setAddingFreeDaysTenantId(null);
    }
  };

  const getStatusBadge = (status: AdminTenantItem['status']) => {
    const config = {
      active: { label: 'Ativo', variant: 'success' as const },
      trial: { label: 'Trial', variant: 'warning' as const },
      expired: { label: 'Expirado', variant: 'danger' as const },
      suspended: { label: 'Suspenso', variant: 'danger' as const },
      cancelled: { label: 'Cancelado', variant: 'default' as const },
    };

    const item = config[status] || { label: status, variant: 'default' as const };
    return <Badge variant={item.variant}>{item.label}</Badge>;
  };

  const filteredTenants = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    if (!normalizedSearch) {
      return tenants;
    }

    return tenants.filter((tenant) => {
      const fields = [
        tenant.name,
        tenant.email,
        ...(tenant.portalDomains || []),
      ]
        .filter(Boolean)
        .map((value) => String(value).toLowerCase());

      return fields.some((value) => value.includes(normalizedSearch));
    });
  }, [tenants, searchTerm]);

  if (!adminToken) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Painel Admin</CardTitle>
            <CardDescription>Acesso restrito ao administrador</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Input label="Usuário" value={username} onChange={(event) => setUsername(event.target.value)} />
            <Input label="Senha" type="password" value={password} onChange={(event) => setPassword(event.target.value)} />
            <Button className="w-full" onClick={handleLogin} isLoading={isLoggingIn} disabled={!username.trim() || !password}>
              Entrar
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Painel Administrativo</h1>
            <p className="text-slate-500 dark:text-slate-300">Gerencie tenants, parceiros e geração de keys.</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => void loadTenants()} isLoading={isLoadingTenants}>Atualizar</Button>
            <Button variant="ghost" onClick={handleLogout}>Sair</Button>
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Tenants</CardTitle>
            <CardDescription>
              {filteredTenants.length} de {tenants.length} tenant(s)
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="mb-4">
              <Input
                label="Buscar por nome, e-mail ou domínio"
                placeholder="Ex.: cliente@empresa.com ou bitrix24.com"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
              />
            </div>

            {isLoadingTenants ? (
              <div className="text-sm text-slate-500 dark:text-slate-300">Carregando tenants...</div>
            ) : filteredTenants.length === 0 ? (
              <div className="text-sm text-slate-500 dark:text-slate-300">Nenhum tenant encontrado.</div>
            ) : (
              <div className="space-y-3">
                {filteredTenants.map((tenant) => (
                  <div key={tenant.id} className="rounded-lg border border-slate-200 dark:border-slate-700 p-4">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <p className="font-semibold text-slate-900 dark:text-slate-100">{tenant.name}</p>
                          {getStatusBadge(tenant.status)}
                          {tenant.isPartner && <Badge variant="info">Parceiro</Badge>}
                        </div>
                        <p className="text-sm text-slate-500 dark:text-slate-300">{tenant.email}</p>
                        <p className="text-xs text-slate-500 dark:text-slate-300">ID: {tenant.id}</p>
                        <p className="text-xs text-slate-500 dark:text-slate-300">
                          Domínios: {tenant.portalDomains?.length ? tenant.portalDomains.join(', ') : 'n/a'}
                        </p>
                        <p className="text-xs text-slate-500 dark:text-slate-300">
                          Plano: {String(tenant.subscriptionPlan || 'free').toUpperCase()} • Limite: {tenant.messageLimit ?? 'n/a'}
                        </p>
                        <p className="text-xs text-slate-500 dark:text-slate-300">
                          Trial até: {tenant.trialEndsAt ? new Date(tenant.trialEndsAt).toLocaleString('pt-BR') : 'n/a'}
                        </p>
                        <p className="text-xs text-slate-500 dark:text-slate-300">
                          Key parceiro: {tenant.partnerKey || 'não gerada'}
                        </p>
                        <p className="text-xs text-slate-500 dark:text-slate-300">
                          Parceiro vinculado: {tenant.partnerTenantId || 'n/a'}
                        </p>
                      </div>

                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          onClick={() => void handleNotifyTenant(tenant.id)}
                          isLoading={notifyingTenantId === tenant.id}
                        >
                          Notificar
                        </Button>
                        <Button
                          onClick={() => void handleGeneratePartnerKey(tenant.id)}
                          isLoading={generatingKeyTenantId === tenant.id}
                        >
                          Gerar key de parceiro
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => void handleUnlinkPartner(tenant)}
                          isLoading={unlinkingPartnerTenantId === tenant.id}
                          disabled={!tenant.isPartner && !tenant.partnerTenantId}
                        >
                          Desvincular parceiro
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => void handleAddFreeDays(tenant)}
                          isLoading={addingFreeDaysTenantId === tenant.id}
                          disabled={tenant.isPartner}
                        >
                          + Dias Free
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
